import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  MoreHorizontal,
  Eye,
  ChevronUp,
  ChevronDown,
  Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Mask } from "@/utils/mask";
import { DeleteBtn } from "@/components/Delete";
import { deleteFinancialReconciliationTransactionData } from "@/Api/reconcialiationFinancialTransactions";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export interface SortConfig {
  column: string;
  direction: "asc" | "desc";
}

interface TableDataProps {
  financialTransactions: any[];
  updateFront: React.Dispatch<React.SetStateAction<ApiDataProps<any>>>;
  dataApi: ApiDataProps<any>;
  pageActions: TypePageActions;
  onSort?: (column: string, direction: "asc" | "desc") => void;
  sortConfig?: SortConfig;
}

export function TableData({
  financialTransactions,
  updateFront,
  dataApi,
  pageActions,
  onSort,
  sortConfig,
}: TableDataProps) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const handleSort = (column: string) => {
    if (!onSort) return;

    const direction =
      sortConfig?.column === column && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";

    onSort(column, direction);
  };

  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return null;
    }
    return sortConfig?.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  return (
    <>
      <div className="rounded-md border">
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo de Concialiação</TableHead>
              <TableHead
                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleSort("start_date")}
              >
                <div className="flex items-center gap-1">
                  <Search className="h-3 w-3 text-gray-400" />
                  Data Inicial{getSortIcon("start_date")}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleSort("end_date")}
              >
                <div className="flex items-center gap-1">
                  <Search className="h-3 w-3 text-gray-400" />
                  Data Final{getSortIcon("end_date")}
                </div>
              </TableHead>
              <TableHead>Autor</TableHead>
              <TableHead className="text-center">Pendentes</TableHead>
              <TableHead className="text-center">Conciliados</TableHead>
              <TableHead className="text-center">Divergentes</TableHead>
              <TableHead className="text-center">Total</TableHead>
              <TableHead className="w-[50px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {financialTransactions?.length > 0 ? (
              financialTransactions?.map((reconciliation) => (
                <TableRow key={reconciliation.id}>
                  <TableCell>{reconciliation.name}</TableCell>
                  <TableCell>
                    <div 
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        reconciliation?.financial_transaction_type?.id === 1 
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" 
                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      }`}
                    >
                      {reconciliation?.financial_transaction_type?.name || "-"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {Mask.transformMaskBirthdayInBr(reconciliation.start_date)}
                  </TableCell>
                  <TableCell>
                    {Mask.transformMaskBirthdayInBr(reconciliation.end_date)}
                  </TableCell>
                  <TableCell>{reconciliation.author?.name || "-"}</TableCell>
                  <TableCell className="text-center">
                    <span className="px-2 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                      {reconciliation?.pending_count || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      {reconciliation?.reconciled_count || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="px-2 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                      {reconciliation?.divergent_count || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium">
                      {reconciliation?.financial_reconciliations_count || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-3 w-full justify-center">
                      <button
                        onClick={() =>
                          navigate(
                            `/reconciliacao-financeira/${reconciliation.uuid}`
                          )
                        }
                        className=" hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-full p-1 transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="size-5" />
                      </button>
                      <If condition={hasPermission("delete-reconciliation")}>
                        <DeleteBtn
                          params={{
                            func: deleteFinancialReconciliationTransactionData,
                            funcParams: reconciliation.uuid,
                          }}
                          updateFront={() =>
                            updateFront((prev) => ({
                              ...prev,
                              data: prev.data.filter(
                                (item) => item.uuid !== reconciliation.uuid
                              ),
                            }))
                          }
                        />
                      </If>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center">
                  Nenhuma conciliação encontrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page <= 1 &&
                      "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    "size-8 bg-transparent border-2 border-blue-600 text-slate-900 dark:text-white font-bold cursor-auto hover:bg-transparent"
                  )}
                >
                  <span className="pointer-events-none">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black dark:text-white h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page >= dataApi?.last_page &&
                      "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
}
